import _ from 'lodash'
import { v4 as uuid } from 'uuid'
import { ConflictError } from '../../internal'

function makeSelectVehicleToRemove({
  vehicleUseHistory
}: {
  vehicleUseHistory: { checkOut?: { reservationVehicleId: string } | null }[]
}) {
  return function selectVehicleToRemove<V extends { id: string }>(existingVehicles: V[]) {
    const notCheckedOutExisting = _.sortBy(
      existingVehicles.filter((e) => !vehicleUseHistory.some((v) => v.checkOut?.reservationVehicleId === e.id)),
      (e) => e.id
    )
    if (!notCheckedOutExisting.length) {
      throw new ConflictError('Cannot update this order as one or more vehicles are checked out')
    } else {
      return notCheckedOutExisting[0].id
    }
  }
}

function getReservationVehiclesFromIntendedAssetFamilyIdsRaw({
  intendedAssetFamilyIds,
  existingVehicles,
  selectVehicleToRemove
}: {
  existingVehicles: { assetFamilyId: string; id: string }[]
  intendedAssetFamilyIds: string[]
  selectVehicleToRemove: (existing: { assetFamilyId: string; id: string }[]) => string
}) {
  const allAssetFamilyIds = _.sortBy(
    [...new Set([...intendedAssetFamilyIds, ...existingVehicles.map((r) => r.assetFamilyId)])],
    (a) => a
  )

  const assetFamilyCounts = allAssetFamilyIds.map((assetFamilyId) => {
    // sort by checked out vehicles first because all logic of deleting/editing based on index in array
    const existing = existingVehicles.filter((v) => v.assetFamilyId === assetFamilyId)
    const intendedCount = intendedAssetFamilyIds.filter((u) => u === assetFamilyId).length
    const existingCount = existing.length

    const countToRemove = Math.max(existing.length - intendedCount, 0)
    const { vehiclesToRetain, removed } = Array.from<undefined>({ length: countToRemove }).reduce(
      (acc) => {
        const toRemove = selectVehicleToRemove(acc.vehiclesToRetain)
        return {
          countToRemove: acc.countToRemove - 1,
          vehiclesToRetain: acc.vehiclesToRetain.filter((e) => e.id !== toRemove),
          removed: [...acc.removed, toRemove]
        }
      },
      { countToRemove, vehiclesToRetain: existing, removed: [] as string[] }
    )

    return {
      assetFamilyId,
      vehiclesToRetain: vehiclesToRetain,
      excessVehiclesToUpdateOrCancel: existing.filter((e) => removed.includes(e.id)),
      additionalVehiclesNeeded: Math.max(intendedCount - existingCount, 0)
    }
  })

  const allExcessVehiclesToUpdateOrCancel = _.sortBy(
    assetFamilyCounts.flatMap((a) => a.excessVehiclesToUpdateOrCancel),
    (v) => v.id
  )
  const totalAdditionalNeeded = _.sumBy(assetFamilyCounts, (m) => m.additionalVehiclesNeeded)
  const reservationsToUpdateInsteadOfCancel = allExcessVehiclesToUpdateOrCancel
    .slice(0, totalAdditionalNeeded)
    .map((r) => r.id)

  const result = assetFamilyCounts.reduce((previous, assetFamily) => {
    const availableUpdateIds = reservationsToUpdateInsteadOfCancel.filter((id) => !previous.some((p) => p.id === id))
    return [
      ...previous,
      ...assetFamily.vehiclesToRetain,
      ...Array.from<string>({ length: assetFamily.additionalVehiclesNeeded })
        .fill(assetFamily.assetFamilyId)
        .map((assetFamilyId, index) => ({
          assetFamilyId,
          id: availableUpdateIds[index] ?? uuid()
        }))
    ]
  }, [] as { assetFamilyId: string; id: string }[])

  return result
}

export const getReservationVehiclesFromIntendedAssetFamilyIds = Object.assign(
  getReservationVehiclesFromIntendedAssetFamilyIdsRaw,
  { makeSelectVehicleToRemove }
)
